import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { teamApi, teamMemberApi } from "@/services/teamService";

// Query hooks
export const useTeams = () => {
  return useQuery({
    queryKey: ["teams"],
    queryFn: teamApi.getAll,
  });
};

export const useTeamMembers = (teamPid: string) => {
  return useQuery({
    queryKey: ["teams", teamPid, "members"],
    queryFn: () => teamMemberApi.getMembers(teamPid),
    enabled: !!teamPid,
  });
};

// Mutation hooks
export const useCreateTeam = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: teamApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
};

export const useUpdateTeam = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ teamPid, data }: { teamPid: string; data: { name: string; description?: string } }) =>
      teamApi.update(teamPid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
};

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: teamApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
};

export const useAddTeamMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ teamPid, userPid }: { teamPid: string; userPid: string }) =>
      teamMemberApi.add(teamPid, userPid),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["teams", variables.teamPid, "members"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
};

export const useRemoveTeamMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ teamPid, userPid }: { teamPid: string; userPid: string }) =>
      teamMemberApi.remove(teamPid, userPid),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["teams", variables.teamPid, "members"] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
};
